"use client";

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  useCreateClosureCommandControllerCreate,
  useUpdateClosureCommandControllerUpdate,
} from "@/generated/closures/closures";
import { CreateClosureDtoType, UpdateClosureDtoType } from "@/generated/model";

export const CLOSURE_TYPE_LABELS: Record<string, string> = {
  [CreateClosureDtoType.BANK_HOLIDAY]: "Bank holiday",
  [CreateClosureDtoType.INSET_TRAINING]: "Inset / training day",
  [CreateClosureDtoType.SEASONAL]: "Seasonal closure",
  [CreateClosureDtoType.AD_HOC]: "Ad-hoc / emergency",
};

export interface ClosureFormValues {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
  reason: string | null;
}

const closureSchema = z
  .object({
    type: z.nativeEnum(CreateClosureDtoType),
    startDate: z.string().min(1, "Start date is required"),
    endDate: z.string().optional(),
    reason: z
      .string()
      .max(255, "Reason must be less than 255 characters")
      .optional(),
  })
  .refine((d) => !d.endDate || d.endDate >= d.startDate, {
    message: "End date must be on or after the start date",
    path: ["endDate"],
  });

interface ClosureFormDialogProps {
  nurseryId: string;
  closure?: ClosureFormValues | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function invalidateClosures(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      typeof query.queryKey[0] === "string" &&
      query.queryKey[0].includes("/closures"),
    refetchType: "all",
  });
}

export function ClosureFormDialog({
  nurseryId,
  closure,
  open,
  onOpenChange,
}: ClosureFormDialogProps) {
  const queryClient = useQueryClient();
  const mode = closure ? "edit" : "create";

  const [type, setType] = useState<string>(CreateClosureDtoType.BANK_HOLIDAY);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (closure && mode === "edit") {
      setType(closure.type);
      setStartDate(closure.startDate);
      setEndDate(closure.endDate);
      setReason(closure.reason ?? "");
      setErrors({});
    }
  }, [closure, mode]);

  useEffect(() => {
    if (!open || mode === "create") {
      setType(CreateClosureDtoType.BANK_HOLIDAY);
      setStartDate("");
      setEndDate("");
      setReason("");
      setErrors({});
    }
  }, [open, mode]);

  const { mutate: createClosure, isPending: isCreating } =
    useCreateClosureCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Closure added");
          await invalidateClosures(queryClient);
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          toast.error(
            error instanceof Error ? error.message : "Failed to add closure",
          );
        },
      },
    });

  const { mutate: updateClosure, isPending: isUpdating } =
    useUpdateClosureCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          toast.success("Closure updated");
          await invalidateClosures(queryClient);
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          toast.error(
            error instanceof Error ? error.message : "Failed to update closure",
          );
        },
      },
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const result = closureSchema.safeParse({
      type,
      startDate,
      endDate: endDate || undefined,
      reason: reason || undefined,
    });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        fieldErrors[err.path.join(".")] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    const common = {
      startDate,
      endDate: endDate || undefined,
      reason: reason.trim() || undefined,
    };

    if (mode === "create") {
      createClosure({
        nurseryId,
        data: { type: type as CreateClosureDtoType, ...common },
      });
    } else if (closure) {
      updateClosure({
        nurseryId,
        closureId: closure.id,
        data: { type: type as UpdateClosureDtoType, ...common },
      });
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-md max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">
          {mode === "edit" ? "Edit closure" : "Add closure"}
        </DialogTitle>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {mode === "edit" ? "Edit closure" : "Add closure"}
              </span>
              <span className="text-body font-body text-subtext-color">
                A non-operating day or date range for this nursery.
              </span>
            </div>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          <div className="flex flex-col gap-6 px-6 py-6">
            <Select
              label="Type"
              value={type}
              onValueChange={setType}
              disabled={isPending}
            >
              {Object.values(CreateClosureDtoType).map((value) => (
                <Select.Item key={value} value={value}>
                  {CLOSURE_TYPE_LABELS[value]}
                </Select.Item>
              ))}
            </Select>

            <TextField
              className="h-auto w-full"
              size="large"
              label="Start date"
              error={!!errors.startDate}
              helpText={errors.startDate}
            >
              <TextField.Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isPending}
              />
            </TextField>

            <TextField
              className="h-auto w-full"
              size="large"
              label="End date (optional)"
              helpText={
                errors.endDate || "Leave blank for a single-day closure."
              }
              error={!!errors.endDate}
            >
              <TextField.Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isPending}
              />
            </TextField>

            <TextArea
              className="h-auto w-full"
              label="Reason (optional)"
              helpText={errors.reason}
              error={!!errors.reason}
            >
              <TextArea.Input
                placeholder="e.g., Christmas closure, Snow day"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isPending}
              />
            </TextArea>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isPending}
            >
              {isPending
                ? mode === "edit"
                  ? "Saving..."
                  : "Adding..."
                : mode === "edit"
                  ? "Save Changes"
                  : "Add Closure"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
